import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { ticketingService } from 'services/api';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { useTicketColumns } from 'components/common/enhanced/common-headers';
import toast from 'features/toast';
import dayjs from 'dayjs';

export const ArchivedTickets = () => {
  const translationPrefix = 'pages.archived-tickets';

  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const toIsoOrNull = (value: unknown): string | null => {
    if (value == null || value === '') return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    // Sometimes DataGrid gives a string representation.
    const raw = String(value).trim();
    if (!raw) return null;

    // ISO / RFC-ish timestamps: let Date try.
    const d1 = new Date(raw);
    if (!Number.isNaN(d1.getTime())) {
      return d1.toISOString();
    }

    // YYYY-MM-DD HH:mm
    const m1 = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);
    if (m1) {
      const [, y, mo, d, h, mi] = m1;
      const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), 0, 0);
      return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
    }

    // DD/MM/YYYY HH:mm
    const m2 = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
    if (m2) {
      const [, d, mo, y, h, mi] = m2;
      const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), 0, 0);
      return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
    }

    return null;
  };

  const handleDelete = (id: number) => {
    ticketingService.delete(id).then(() => {
      toast.success(t('actions.successfully.deleted'));
      datagridRefresh.current();
    });
  };

  const columns = useTicketColumns({ handleDelete });

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}>
        <EnhancedDataGrid
          columns={columns}
          service={ticketingService}
          getMethod={ticketingService.getClosed}
          refresh={datagridRefresh}
          disableDelete
          onCellEditCommitOverride={async (params, _event, apiRef) => {
            // Only override resolved_at edit in archived tickets.
            if (params.field !== 'resolved_at') {
              const payload: Record<string, unknown> = { [params.field]: params.value };
              if (params.value instanceof Date) {
                payload[params.field] = dayjs(params.value).format('YYYY-MM-DD');
              }
              await ticketingService.update(params.id as number, payload);
              toast.success(t('toasts.updated-cell'));
              datagridRefresh.current?.();
              return;
            }

            const row = apiRef?.current?.getRow?.(params.id);
            const rawStatus = row?.status;
            let statusValue: number | null = null;
            if (typeof rawStatus === 'number') {
              statusValue = rawStatus;
            } else if (rawStatus?.value != null) {
              statusValue = rawStatus.value;
            }

            if (statusValue == null) {
              toast.error('Impossibile aggiornare: status non disponibile');
              return;
            }

            const resolvedIso = toIsoOrNull(params.value);
            if (!resolvedIso) {
              toast.error('Data chiusura non valida');
              return;
            }

            await ticketingService.closeTicket(params.id as number, {
              status: statusValue,
              resolved_at: resolvedIso
            });
            toast.success(t('toasts.updated-cell'));
            datagridRefresh.current?.();
          }}
          initialOptions={{
            pinnedColumns: { left: [], right: [] }
          }}
        />
      </TenantPage>
    </>
  );
};

export default ArchivedTickets;
